/**
 * Split Text — vanilla 逐字拆字（SplitText 復刻，不引 React/GSAP）
 *
 * v2（2026-06-01）：逐字進場從 hero 大標題移交三個 section 標題（捲動進場）。
 *   - hero__title 改回「整塊 fade-up」（hero.css 自管，本檔不再掃描）。
 *   - 三 section 標題（categories / clinics / articles）拆成單字元 <span class="split-char">，
 *     每 span 帶 --char-index；IntersectionObserver 進視窗時加 .is-split-in 觸發逐字 stagger，
 *     一次性（once）。CSS 進場規則見 utilities/reveal.css。
 *
 * v3（2026-06-05）：about「卷宗 Dossier」開卷大標 set-piece 加入逐字進場。
 *   - #hero-title-en（ON RECORD）/ #hero-title-zh（立此存證）兩行各自拆 .split-char；
 *     兩 span 同在 about h1 內，各帶 aria-label 念完整文字（h1 整體仍可讀）。
 *   - 進場參數（blur-in / 1200ms / stagger 60ms）由 about.css 的 .about-hero-dossier scope
 *     覆寫 generic .split-char（本檔只負責拆字 + 觸發 .is-split-in，不管視覺參數）。
 *   - hero 在首屏：IntersectionObserver 對「初次即相交」元素會立即回呼 → 等同 page-load 觸發。
 *   - 註（2026-07-15 讀碼發現，未動）：about 頁 hero 已改版為全站共用 .page-hero-v2
 *     （about.html/about.php 現況，.hero-immersion 沉浸層與開卷大標已撤），#hero-title-en /
 *     #hero-title-zh 目前在任何頁面 DOM 中都已不存在，SPLIT_TARGET_SELECTOR 對它們的
 *     querySelectorAll 恆回空集合（無害，純選擇器層面的死碼）。不在本次修復範圍內清理，
 *     原樣保留，僅留註記避免後人誤以為這兩個 target 仍在運作。
 *
 * v4（2026-07-15）：補雙重安全網，修「快速滑動/甩動滑過標題區塊時 .split-char 永久卡
 *   opacity:0」的 bug（quality agent 45 次跨瀏覽器測試證實，18/18 次重現）。原本單一
 *   IntersectionObserver 無 timeout / scroll fallback：快速捲動時，瀏覽器可能整段跳過
 *   「交集比例跨越 threshold」的中繼影格（畫面從「標題在視窗下方」直接跳到「已捲出視窗
 *   上方」，起訖交集比例皆為 0），intersecting 從未被觸發 → .is-split-in 永不加上。
 *   兩層安全網皆為純加法、idempotent（只對 :not(.is-split-in) 動作），互不衝突：
 *   - 安全網 1（scrolledPast，比照 carousel.js setupCardReveal 的 flushIfScrolledPast
 *     手法）：逐一監聽每個 target 自身 getBoundingClientRect().bottom <= 0（已完整捲出
 *     視窗上緣），成立即強制顯示。精準對應本 bug 實際失效條件，只在使用者真的已捲過該
 *     標題時動作，不影響正常速度捲動使用者的原生進場動畫時機（未捲過的 target 條件恆
 *     為 false，不會提前搶動作）。
 *   - 安全網 2（SAFETY_FLUSH_MS，比照 scroll-reveal.js 全域逾時手法）：3000ms 後強制
 *     顯示任何殘留未進場的 target，涵蓋 scrolledPast 顧不到的情境（IO 因故從未回呼，
 *     使用者也從未捲過該標題，例如首屏即可見的 target 若使用者全程未捲動頁面）。
 *   兩層皆只掛在既有 IntersectionObserver 分支（非 PRM、瀏覽器支援 IO）內，PRM / 老瀏覽器
 *   分支不受影響——維持原本直接顯示、不建立任何 observer / timer / listener，reduced-motion
 *   行為與修復前完全一致。
 *
 * a11y：標題設 aria-label 念完整文字，char span 全 aria-hidden（閱讀器不逐字唸）。
 * 冪等：已拆過（.is-split）直接 return，避免重複 innerHTML 把 span 再拆一層。
 */

// P-H-3：改 import 共用 prefersReducedMotion()（utils/dom.js），去重原 inline matchMedia 判斷。
import { prefersReducedMotion } from './utils/dom.js';

// 逐字進場目標：三 section 標題（捲動進場）+ about 開卷大標兩行（首屏 set-piece）。
// hero__title（首頁 hero）不在此列（改回整塊淡入，hero.css 自管）。
const SPLIT_TARGET_SELECTOR = '#categories-title, #clinics-title, #articles-title, #hero-title-en, #hero-title-zh';

const PRM = prefersReducedMotion();

/**
 * 把標題文字拆成單字元 span。char span 全 aria-hidden，h2 設 aria-label 念完整文字。
 * @param {HTMLElement} el 標題元素
 */
function splitTitle(el) {
  if (el.classList.contains('is-split')) return; // 冪等
  const text = el.textContent.trim();
  el.dataset.splitText = text;
  el.setAttribute('aria-label', text); // a11y：閱讀器念完整文字
  const html = [...text]
    .map((ch, i) => {
      const isSpace = ch === ' ';
      const cls = isSpace ? 'split-char split-char--space' : 'split-char';
      const glyph = isSpace ? '&nbsp;' : ch;
      return `<span class="${cls}" aria-hidden="true" style="--char-index:${i}">${glyph}</span>`;
    })
    .join(''); // join('') 不留空白（防 inline-block 縫隙）
  el.innerHTML = html;
  el.classList.add('is-split');
}

const targets = document.querySelectorAll(SPLIT_TARGET_SELECTOR);

// 先拆字（同步），確保進場觀察前 span 已就位。
targets.forEach(splitTitle);

if (PRM || !('IntersectionObserver' in window)) {
  // PRM / 老瀏覽器：直接定格可見（CSS reduced-motion 分支亦會直顯，雙保險冪等）。
  targets.forEach((el) => el.classList.add('is-split-in'));
} else if (targets.length) {
  // targets.length 才建 observer / 安全網：頁面若無任何符合選擇器的標題（多數內頁），
  // 完全不掛 scroll listener / timer，避免全站每頁都白付這筆成本。

  /**
   * 標記進場 + 停止觀察。IO 回呼與下方兩層安全網共用，避免三處各自重複「加 class +
   * unobserve」邏輯；冪等（重複呼叫同一 el 安全：class 已存在則 add() 無效果，
   * unobserve 對已 unobserve 的 target 也是無害 no-op）。function 宣告（非 arrow）
   * 讓下方 observer / 安全網程式碼在檔案順序上可放心引用，不受 const 的 TDZ 影響。
   */
  function reveal(el) {
    el.classList.add('is-split-in');
    observer.unobserve(el);
  }

  // 捲動進視窗時逐字進場，一次性 unobserve。threshold/rootMargin 對齊 scroll-reveal.js。
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        reveal(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -10% 0px',
  });
  targets.forEach((el) => observer.observe(el));

  const remainingTargets = () => [...targets].filter((el) => !el.classList.contains('is-split-in'));

  /* ---------- 安全網 1：scrolledPast（比照 carousel.js flushIfScrolledPast）----------
   * 見上方檔頭 v4 說明。逐一檢查每個殘留 target 是否已完整捲出視窗上緣，是則直接補顯示。
   */
  const flushIfScrolledPast = () => {
    const remaining = remainingTargets();
    if (!remaining.length) {
      window.removeEventListener('scroll', onScrolledPast);
      return;
    }
    remaining.forEach((el) => {
      if (el.getBoundingClientRect().bottom <= 0) {
        reveal(el);
      }
    });
    if (!remainingTargets().length) {
      window.removeEventListener('scroll', onScrolledPast);
    }
  };

  // rAF 節流：scroll 高頻，避免每幀重複查 DOM（比照 scroll-reveal.js / carousel.js 既有寫法）。
  let pastTicking = false;
  function onScrolledPast() {
    if (pastTicking) return;
    pastTicking = true;
    requestAnimationFrame(() => {
      flushIfScrolledPast();
      pastTicking = false;
    });
  }

  window.addEventListener('scroll', onScrolledPast, { passive: true });
  // 立即補查一次（不等下一次 scroll 事件）：涵蓋本檔初始化時機晚於使用者已完成捲動的
  // 情境（bfcache 還原、hash 錨點跳過本區塊）。
  requestAnimationFrame(flushIfScrolledPast);

  /* ---------- 安全網 2：全域 safety timeout（比照 scroll-reveal.js SAFETY_FLUSH_MS）----------
   * 見上方檔頭 v4 說明。scrolledPast 只覆蓋「已被捲過」的情境；IO 因故從未回呼、使用者也
   * 從未捲過該標題時（如首屏可見但使用者全程未捲動），逾時做最後一次 flush。
   */
  const SAFETY_FLUSH_MS = 3000;
  window.setTimeout(() => {
    remainingTargets().forEach(reveal);
  }, SAFETY_FLUSH_MS);

  // cleanup：頁面卸載 / bfcache 時斷開觀察，避免殘留 observer / listener。
  window.addEventListener('pagehide', () => {
    observer.disconnect();
    window.removeEventListener('scroll', onScrolledPast);
  }, { once: true });
}
